import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotification } from '../hooks/useNotification';
import { Card, Text, LoadingSpinner, tokens } from '../design-system';
import apiClient from '../services/unifiedApiClient';

const GitHubCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Completing GitHub App installation...');

  useEffect(() => {
    const completeInstallation = async () => {
      const installationId = searchParams.get('installation_id');
      const setupAction = searchParams.get('setup_action') || 'install';
      const state = searchParams.get('state');

      if (!installationId) {
        setStatus('error');
        setMessage('Missing installation ID from GitHub.');
        return;
      }

      try {
        const data = await apiClient.get('/github/app/callback', {
          params: {
            installation_id: installationId,
            setup_action: setupAction,
            state: state,
          },
        });

        if (data.success) {
          showNotification({
            type: 'success',
            message: `GitHub App installed for ${data.account_login || 'your account'}`,
          });
          setStatus('success');
          setMessage('GitHub App installed successfully! Redirecting...');
          // Redirect to return_to path (with github_connected flag) or Settings
          const returnTo = data.return_to;
          const redirectPath = returnTo
            ? `${returnTo}${returnTo.includes('?') ? '&' : '?'}github_connected=true`
            : '/settings';
          setTimeout(() => navigate(redirectPath), 1500);
        } else {
          setStatus('info');
          setMessage(data.message || 'Installation pending approval.');
          setTimeout(() => navigate('/settings'), 3000);
        }
      } catch (error) {
        console.error('GitHub callback error:', error);
        setStatus('error');
        setMessage(
          error.response?.data?.detail ||
          error.userMessage ||
          'Failed to complete GitHub App installation.'
        );
      }
    };

    completeInstallation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      padding: tokens.spacing[8],
    }}>
      <Card style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
        {status === 'processing' && (
          <>
            <LoadingSpinner size="lg" />
            <Text style={{ marginTop: tokens.spacing[4] }}>{message}</Text>
          </>
        )}
        {status === 'success' && (
          <>
            <Text size="lg" weight="bold" style={{ color: tokens.colors.success[500] }}>
              {message}
            </Text>
          </>
        )}
        {status === 'error' && (
          <>
            <Text size="lg" weight="bold" style={{ color: tokens.colors.error[500], marginBottom: tokens.spacing[4] }}>
              Installation Failed
            </Text>
            <Text>{message}</Text>
            <button
              onClick={() => navigate('/settings')}
              style={{
                marginTop: tokens.spacing[4],
                padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                cursor: 'pointer',
                backgroundColor: tokens.colors.primary[500],
                color: 'white',
                border: 'none',
                borderRadius: tokens.borderRadius.md,
              }}
            >
              Back to Settings
            </button>
          </>
        )}
        {status === 'info' && (
          <>
            <Text size="lg" weight="bold" style={{ marginBottom: tokens.spacing[4] }}>
              Pending Approval
            </Text>
            <Text>{message}</Text>
          </>
        )}
      </Card>
    </div>
  );
};

export default GitHubCallback;
